import type {
  GroupRole,
  PageAccessStrategy,
  PagePermission,
} from "@/types/domain";

export type PageAction = "view" | "edit" | "delete" | "manage_access";

const capabilities: Record<GroupRole, ReadonlySet<PageAction>> = {
  owner: new Set(["view", "edit", "delete", "manage_access"]),
  admin: new Set(["view", "edit", "delete", "manage_access"]),
  editor: new Set(["view", "edit"]),
  viewer: new Set(["view"]),
};

export function canRole(role: GroupRole | undefined, action: PageAction) {
  return role ? capabilities[role].has(action) : false;
}

export function resolvePageAccess({
  userId,
  role,
  permission,
}: {
  userId: string;
  role: GroupRole;
  permission?: Pick<
    PagePermission,
    "strategy" | "memberOverrides" | "inheritToChildren"
  >;
}): "none" | "view" | "edit" {
  if (role === "owner") {
    return "edit";
  }

  const baseAccess = canRole(role, "edit") ? "edit" : "view";
  if (!permission || permission.strategy === "inherit_group") {
    return baseAccess;
  }

  const override = permission.memberOverrides.find(
    (entry) => entry.userId === userId,
  );
  if (override) {
    return override.access === "edit" && role === "viewer"
      ? "view"
      : override.access;
  }

  return strategyDefault(permission.strategy, role, baseAccess);
}

function strategyDefault(
  strategy: PageAccessStrategy,
  role: GroupRole,
  baseAccess: "view" | "edit",
): "none" | "view" | "edit" {
  if (strategy === "allow_selected") {
    return role === "admin" ? baseAccess : "none";
  }
  if (strategy === "owner_admin_only") {
    return role === "admin" ? "edit" : "none";
  }
  return baseAccess;
}

export function canManageMembers(role: GroupRole | undefined) {
  return role === "owner" || role === "admin";
}

export function canManageGroup(role: GroupRole | undefined) {
  return role === "owner" || role === "admin";
}
